import { reactive } from 'vue';
import type { Ref } from 'vue';

import { getCenter } from 'ol/extent';
import { Geometry } from 'ol/geom';
import type { SimpleGeometry } from 'ol/geom';
import type { Coordinate } from 'ol/coordinate';

import type { OlMapWithViewEvents } from '@/components/map/events/useMapZoom';
import type { MapFeature } from '@/components/map/features/MapFeatures';


type TooltipStyle = {
	left: string;
	top: string;
};

type XYasArray = readonly [number, number];

type UseMapTooltipResult = {	tooltipStyle: TooltipStyle;
	updateTooltipCoords: (hovered: MapFeature) => void;
};


const toXYasArray = (coord: Coordinate): XYasArray => {
	const [x, y] = coord;
	if (x === undefined || y === undefined) {
		throw new Error('Invalid coordinate length: ' + String(coord.length));
	}
	return [x, y];
};


const isSimpleGeometry = (geometry: Geometry): geometry is SimpleGeometry => (
	'getCoordinates' in geometry && typeof geometry.getCoordinates === 'function'
);


const resolveTooltipCoordinate = (geometry: Geometry): Coordinate | null => {
	const geometryType = geometry.getType();
	if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
		return getCenter(geometry.getExtent());
	}
	if (!isSimpleGeometry(geometry)) {
		return null;
	}
	const coordinates = geometry.getCoordinates();
	if (coordinates == null || !Array.isArray(coordinates) || coordinates.length === 0) {
		return null;
	}
	const first = coordinates[0];
	if (Array.isArray(first)) {
		const start = coordinates[0];
		const end = coordinates[1];
		if (!Array.isArray(start) || !Array.isArray(end)) {
			return null;
		}
		const startX = start[0];
		const startY = start[1];
		const endX = end[0];
		const endY = end[1];
		if (
			startX === undefined || startY === undefined
			|| endX === undefined || endY === undefined
		) {
			return null;
		}
		return [(startX + endX) / 2, Math.max(startY, endY)];
	}
	if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
		return [coordinates[0], coordinates[1]];
	}
	return null;
};


export {
	resolveTooltipCoordinate,
};


export function useMapTooltip(olMap: Ref<OlMapWithViewEvents | null>): UseMapTooltipResult {
	const tooltipStyle = reactive<TooltipStyle>({
		left: '0',
		top: '0',
	});
	const updateTooltipCoords = (hovered: MapFeature): void => {
		const mapInstance = olMap.value;
		if (mapInstance == null) {
			return;
		}
		const geometry = hovered.getGeometry();
		if (geometry == null || !(geometry instanceof Geometry)) {
			return;
		}
		const coordinates = resolveTooltipCoordinate(geometry);
		if (coordinates == null) {
			return;
		}
		const pixel = toXYasArray(mapInstance.getPixelFromCoordinate(coordinates));
		tooltipStyle.left = String(pixel[0]) + 'px';
		tooltipStyle.top = String(pixel[1]) + 'px';
	};
	return {
		tooltipStyle,
		updateTooltipCoords,
	};
}
